/**
 * executionAsynchrone — le vocabulaire commun de l'exécuteur canvas quand le
 * Document API ne rend plus ses résultats sur-le-champ (#939).
 *
 * ## Pourquoi ce module existe AVANT la montée de version
 *
 * En `superdoc@1.44.1`, toutes les opérations du Document API sont synchrones :
 * entre la capture d'une époque et la mutation qu'elle protège, il n'existe
 * aucun point de suspension. Les gardes d'asynchronisme y seraient donc
 * **vertes par construction**, c'est-à-dire intestables. Elles sont écrites ici
 * contre une façade qui *tolère* la promesse (`PeutEtreUnePromesse`), et
 * prouvées contre un exécuteur factice asynchrone qui provoque l'entrelacement
 * de chacune — jamais contre le runtime installé.
 *
 * ## Le jeton d'époque ÉTEND un patron existant, il n'en crée pas un second
 *
 * Le canvas prescrit déjà de **relire** ce qui compte après chaque `await`
 * plutôt que de s'appuyer sur une valeur capturée à la fermeture : la possession
 * du canvas se relit dans les gestionnaires (`isCanvasSessionProtectedByWork`,
 * cf. `canvasSessionProtection.ts`), et le point d'écriture relit son époque de travail à
 * l'entrée ET après l'attente de chaque maillon (`#barrage`,
 * `editor/canvasWritePoint.ts`). Le jeton d'époque est le même geste appliqué à
 * l'écriture agent : capturé au début du callback, ré-asserté avant chaque appel
 * mutant, avant l'émission du receipt et avant le rafraîchissement de révision.
 *
 * Deux gardes voisines qui vérifieraient des choses différentes après le même
 * `await`, c'est la garantie qu'un jour l'une sera oubliée — d'où un seul
 * vérificateur, porté par le contexte d'action et appelé aux mêmes endroits.
 */

/** Une opération du Document API peut rendre une promesse (V2) ou une valeur (1.44.1). */
export type PeutEtreUnePromesse<T> = T | Promise<T>;

/**
 * L'identité du montage sur lequel une action a été autorisée à agir.
 *
 * ⚠️ **Les deux champs sont REQUIS depuis #1336** : `(conversationId,
 * mountEpoch)` EST l'identité d'un montage — la conversation possède au plus un
 * document, et l'époque compte ses montages (elle bouge même quand le même
 * document est rechargé sur lui-même, ce qui invalide toute référence de bloc
 * capturée). Aucun des deux ne désigne seul : l'époque est allouée par un
 * compteur d'onglet que deux conversations peuvent traverser, et la
 * conversation ne distingue pas deux montages successifs du même fil.
 *
 * Ce que la disparition de `documentSessionId` change : il n'existe plus de
 * troisième valeur à comparer, donc plus de couple qui puisse diverger. Ce que
 * la disparition de l'optionalité change : une époque absente n'est plus « je
 * ne sais pas », elle est un refus — voir `canvasAgentRequestGuard`.
 */
export interface JetonDEpoque {
  readonly conversationId: string;
  readonly mountEpoch: number;
}

/** Vrai quand les deux jetons désignent le même montage de la même conversation. */
export function memeEpoque(attendue: JetonDEpoque, courante: JetonDEpoque | null): boolean {
  return courante !== null
    && courante.conversationId === attendue.conversationId
    && courante.mountEpoch === attendue.mountEpoch;
}

/**
 * Le document sous l'action a changé pendant une attente.
 *
 * Porte `mutationDejaTentee` parce que les deux cas n'ont pas le même verdict :
 * avant toute mutation, l'action est simplement **refusée** (rien n'a bougé) ;
 * après, elle est **non vérifiable** — on ne peut plus relire le document où
 * l'effet aurait atterri, donc l'affirmer réussi comme échoué serait mentir.
 */
export class EpoqueDepasseeError extends Error {
  constructor(
    readonly attendue: JetonDEpoque,
    readonly courante: JetonDEpoque | null,
    readonly etape: string,
    readonly mutationDejaTentee: boolean,
  ) {
    super(
      `Le document a changé pendant l'action canvas (étape « ${etape} ») : `
      + `attendu conversationId=${attendue.conversationId} mountEpoch=${attendue.mountEpoch}, `
      + `monté ${courante === null ? 'aucun document' : `conversationId=${courante.conversationId} mountEpoch=${courante.mountEpoch}`}. `
      + (mutationDejaTentee
        ? "Une mutation avait déjà été tentée : son effet n'est PAS vérifiable sur le document actuel."
        : "Aucune mutation n'avait été tentée : le document n'a pas été touché."),
    );
    this.name = 'EpoqueDepasseeError';
  }
}

/**
 * LE prédicat d'arrêt de lot — il n'en existe qu'un, et c'est délibéré.
 *
 * Les `catch` par élément (une suppression par bloc, une facette de style par
 * appel) existent pour qu'un élément cassé n'emporte pas les autres. Ils ne
 * doivent JAMAIS avaler un arrêt de lot : continuer à écrire dans un document
 * qui a changé sous l'action est exactement ce que la garde d'époque interdit.
 * Chaque `catch` de ce genre appelle donc cette fonction, plutôt que de
 * re-comparer un nom de classe pour son compte — une seconde écriture du même
 * contrôle serait celle qu'on oublierait de mettre à jour.
 */
export function estArretDeLot(erreur: unknown): erreur is EpoqueDepasseeError {
  return erreur instanceof EpoqueDepasseeError;
}

export function estEpoqueDepassee(erreur: unknown): erreur is EpoqueDepasseeError {
  return estArretDeLot(erreur);
}

/**
 * Ce que le contexte d'action appelle aux frontières d'`await`.
 *
 * `etape` nomme le point de contrôle dans le message d'erreur : un futur
 * lecteur doit savoir SI la mutation avait commencé sans relire le code.
 */
export type VerificateurDEpoque = (etape: string) => void;

/**
 * Construit le vérificateur à partir d'une lecture **fraîche** de l'époque
 * courante — jamais d'une valeur capturée : c'est tout l'objet de la garde.
 */
export function creerVerificateurDEpoque(
  attendue: JetonDEpoque,
  lireEpoqueCourante: () => JetonDEpoque | null,
  etatMutation: { tentee: boolean },
): VerificateurDEpoque {
  return (etape: string) => {
    const courante = lireEpoqueCourante();
    if (!memeEpoque(attendue, courante)) {
      throw new EpoqueDepasseeError(attendue, courante, etape, etatMutation.tentee);
    }
  };
}

/**
 * Capture le rejet d'un signal lancé sans être attendu (#939).
 *
 * ⚠️ Load-bearing, pas de la politesse : les signaux de disponibilité du canvas
 * sont appelés depuis des callbacks React qui n'attendent rien. La lecture de
 * révision étant devenue asynchrone, ils rendent désormais une promesse — et un
 * rejet non capturé fait tomber tout le processus (`unhandledRejection`), là où
 * la version synchrone ne faisait qu'échouer localement.
 *
 * Le message NOMME le signal : sans lui, la trace se réduit à une erreur de
 * lecture SuperDoc sans le moindre indice sur qui l'attendait.
 */
export function capturerLeRejet(promesse: Promise<unknown>, signal: string): void {
  void promesse.catch((erreur: unknown) => {
    // eslint-disable-next-line no-console
    console.warn(
      `[canvas] le signal de disponibilité « ${signal} » a échoué — l'état du document n'a pas été `
      + 'annoncé pour ce montage.',
      erreur,
    );
  });
}
